import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Add,
  GroupsOutlined,
  KeyboardArrowDown,
  Remove,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import type {
  RuteroConnectorOccupancyLimits,
  RuteroOccupancy,
} from '@bandido/shared/types';
import { RUTERO_HOLD_PASSENGER_MAX } from '@bandido/shared/types';

import {
  circuitOccupancyCount,
  circuitOccupancyValid,
} from '../circuitOccupancy';
import { releaseNumberInputOnWheel } from '../numberInput';

interface Props {
  value: RuteroOccupancy;
  limits: RuteroConnectorOccupancyLimits;
  onChange: (occupancy: RuteroOccupancy) => void;
}

export function CircuitOccupancyPicker({ value, limits, onChange }: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const total = circuitOccupancyCount(value);
  const valid = circuitOccupancyValid(value, limits);
  const maxPassengers = Math.min(
    limits.maxPax ?? RUTERO_HOLD_PASSENGER_MAX,
    RUTERO_HOLD_PASSENGER_MAX,
  );
  const canAddPassenger = total < maxPassengers;

  const updateRoom = (roomIndex: number, ages: number[]) =>
    onChange({
      rooms: value.rooms.map((room, index) =>
        index === roomIndex ? { ...room, ages } : room,),
    });

  return (
    <>
      <ButtonBase
        aria-label={t('travelServices.circuits.occupancy.label')}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="dialog"
        aria-invalid={!valid}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={(theme) => ({
          width: '100%',
          minWidth: 0,
          minHeight: 28,
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 0.75,
          borderRadius: 0.5,
          textAlign: 'left',
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <GroupsOutlined fontSize="small" color="action" />
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          color={valid ? 'text.primary' : 'error.main'}
          sx={{ minWidth: 0, flex: 1 }}
        >
          {t('travelServices.circuits.occupancy.summary', {
            rooms: value.rooms.length,
            passengers: total,
          })}
        </Typography>
        <KeyboardArrowDown fontSize="small" color="action" />
      </ButtonBase>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 440 },
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(660px, calc(100vh - 32px))',
            mt: 0.75,
            borderRadius: 2,
          },
        }}
      >
        <Box p={2} display="flex" flexDirection="column" gap={1.5}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('travelServices.circuits.occupancy.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.circuits.occupancy.hint')}
            </Typography>
          </Box>

          {value.rooms.map((room, roomIndex) => (
            <Box key={roomIndex} display="flex" flexDirection="column" gap={1}>
              {roomIndex > 0 && <Divider />}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" fontWeight={700}>
                  {t('travelServices.circuits.occupancy.room', {
                    number: roomIndex + 1,
                  })}
                </Typography>
                {value.rooms.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      onChange({
                        rooms: value.rooms.filter(
                          (_, index) => index !== roomIndex,
                        ),
                      })
                    }
                  >
                    {t('travelServices.circuits.occupancy.removeRoom')}
                  </Button>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                {room.ages.map((age, occupantIndex) => (
                  <Box
                    key={occupantIndex}
                    display="flex"
                    gap={1}
                    alignItems="center"
                  >
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      label={t('travelServices.circuits.occupancy.age', {
                        number: occupantIndex + 1,
                      })}
                      value={age}
                      inputProps={{
                        min: 0,
                        max: 120,
                        onWheel: releaseNumberInputOnWheel,
                      }}
                      onChange={(event) => {
                        const nextAge = Number(event.target.value);
                        if (
                          !Number.isInteger(nextAge) ||
                          nextAge < 0 ||
                          nextAge > 120
                        ) return;
                        updateRoom(
                          roomIndex,
                          room.ages.map((current, index) =>
                            index === occupantIndex ? nextAge : current,),
                        );
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label={t(
                        'travelServices.circuits.occupancy.removePassenger',
                      )}
                      disabled={room.ages.length === 1}
                      onClick={() =>
                        updateRoom(
                          roomIndex,
                          room.ages.filter(
                            (_, index) => index !== occupantIndex,
                          ),
                        )
                      }
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Button
                size="small"
                startIcon={<Add />}
                disabled={
                  !canAddPassenger ||
                  room.ages.length >= (limits.maxPaxPerRoom ?? 9)
                }
                onClick={() =>
                  updateRoom(roomIndex, [...room.ages, limits.defaultAdultAge])
                }
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('travelServices.circuits.occupancy.addAdult')}
              </Button>
            </Box>
          ))}

          <Box display="flex" justifyContent="space-between" gap={1}>
            <Button
              size="small"
              startIcon={<Add />}
              disabled={
                value.rooms.length >= limits.maxRooms || !canAddPassenger
              }
              onClick={() =>
                onChange({
                  rooms: [...value.rooms, { ages: [limits.defaultAdultAge] }],
                })
              }
            >
              {t('travelServices.circuits.occupancy.addRoom')}
            </Button>
            <Button
              variant="contained"
              disabled={!valid}
              onClick={() => setAnchorEl(null)}
            >
              {t('travelServices.passengerPicker.done')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
